<template>
  <div class="container py-4 trick-sync-view">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
      <div>
        <h2 class="mb-1">Tricklist CSV Sync</h2>
        <p class="text-muted mb-0">
          Compara la tricklist efectiva activa con el CSV del servidor y sincroniza diferencias o nuevos trucos.
        </p>
      </div>

      <div class="d-flex flex-wrap gap-2">
        <button type="button" class="btn btn-outline-secondary" @click="goBackToTrickList">
          <i class="fa-solid fa-arrow-left me-2"></i>Volver a tricklist
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="fetchComparison">
          <i class="fa-solid fa-rotate me-2"></i>{{ loading ? 'Recargando...' : 'Recargar comparación' }}
        </button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert alert-danger mb-4" role="alert">
      {{ errorMessage }}
    </div>

    <div class="row g-3 mb-4">
      <div class="col-12 col-md-4">
        <div class="card h-100 shadow-sm">
          <div class="card-body">
            <div class="small text-uppercase text-muted mb-1">Versión activa</div>
            <div class="fw-semibold">{{ versionLabel }}</div>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="card h-100 shadow-sm">
          <div class="card-body">
            <div class="small text-uppercase text-muted mb-1">CSV</div>
            <div class="fw-semibold text-break mb-3">{{ csvPathLabel }}</div>
            <label class="form-label">Selector de CSV</label>
            <select v-model="selectedCsvFile" class="form-select mb-3">
              <option v-for="file in availableCsvFiles" :key="file.name" :value="file.name">
                {{ file.name }}
              </option>
            </select>
            <label class="form-label">Subir nuevo CSV</label>
            <input class="form-control mb-2" type="file" accept=".csv,text/csv" @change="onCsvUploadSelected">
            <button type="button" class="btn btn-outline-primary w-100" :disabled="!csvUploadFile || uploadingCsv" @click="uploadCsvFile">
              <i class="fa-solid fa-upload me-2"></i>{{ uploadingCsv ? 'Subiendo...' : 'Añadir CSV' }}
            </button>
          </div>
        </div>
      </div>
      <div class="col-12 col-md-4">
        <div class="card h-100 shadow-sm">
          <div class="card-body">
            <div class="small text-uppercase text-muted mb-1">Resumen</div>
            <div class="d-flex flex-wrap gap-2 mt-2">
              <span class="badge text-bg-light">Coinciden: {{ summary.match }}</span>
              <span class="badge text-bg-warning">Diferencias: {{ summary.diff }}</span>
              <span class="badge text-bg-primary">Nuevos CSV: {{ summary.new_csv_only }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-12 col-lg-6">
            <label class="form-label">Buscar por id o nombre</label>
            <input v-model.trim="searchQuery" type="text" class="form-control" placeholder="Ej. 25 o Butt Bounce">
          </div>
          <div class="col-6 col-lg-3">
            <label class="form-label">Estado</label>
            <select v-model="statusFilter" class="form-select">
              <option value="">Todos</option>
              <option value="match">Coinciden</option>
              <option value="diff">Diferencias</option>
              <option value="new_csv_only">Nuevos CSV</option>
            </select>
          </div>
          <div class="col-6 col-lg-3">
            <label class="form-label">Filas</label>
            <div class="form-control bg-light">{{ filteredRows.length }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status" aria-hidden="true"></div>
      <p class="mt-3 mb-0">Cargando comparación...</p>
    </div>

    <div v-else-if="!filteredRows.length" class="alert alert-secondary">
      No hay filas para mostrar con los filtros actuales.
    </div>

    <div v-else class="sync-rows d-grid gap-3">
      <article
        v-for="row in filteredRows"
        :key="row.id"
        class="card shadow-sm sync-row"
        :class="rowClass(row.status)"
      >
        <div class="card-body">
          <div
            class="sync-row__header d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3"
            role="button"
            tabindex="0"
            @click="toggleRow(row.id)"
            @keydown.enter.prevent="toggleRow(row.id)"
            @keydown.space.prevent="toggleRow(row.id)"
          >
            <div class="d-flex flex-wrap align-items-center gap-2">
              <span class="badge rounded-pill text-bg-dark">ID {{ row.id }}</span>
              <span class="badge rounded-pill" :class="statusBadgeClass(row.status)">{{ statusLabel(row.status) }}</span>
              <span v-if="row.differences?.length" class="badge rounded-pill text-bg-warning text-dark">
                {{ row.differences.length }} diferencias
              </span>
            </div>

            <div class="d-flex flex-wrap gap-2">
              <button
                v-if="row.actions?.update"
                type="button"
                class="btn btn-sm btn-warning"
                :disabled="isBusy(row.id)"
                @click.stop="syncRow(row, 'update')"
              >
                <i class="fa-solid fa-pen-to-square me-1"></i>{{ isBusy(row.id) ? 'Actualizando...' : 'Actualizar' }}
              </button>
              <button
                v-if="row.actions?.add"
                type="button"
                class="btn btn-sm btn-primary"
                :disabled="isBusy(row.id)"
                @click.stop="syncRow(row, 'add')"
              >
                <i class="fa-solid fa-plus me-1"></i>{{ isBusy(row.id) ? 'Añadiendo...' : 'Añadir' }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click.stop="toggleRow(row.id)"
              >
                <i class="fa-solid" :class="isRowExpanded(row.id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </button>
            </div>
          </div>

          <div v-if="isRowExpanded(row.id)" class="row g-3 mt-1">
            <div class="col-12 col-xl-6">
              <section class="sync-column sync-column--csv h-100">
                <div class="sync-column__title">CSV</div>
                <dl class="sync-field-list mb-0">
                  <div v-for="field in supportedFieldsForRow(row.csv)" :key="`csv-${row.id}-${field}`" class="sync-field" :class="fieldDiffClass(row, field)">
                    <dt>{{ fieldLabel(field) }}</dt>
                    <dd>{{ formatFieldValue(field, row.csv?.[field]) }}</dd>
                  </div>
                </dl>
              </section>
            </div>

            <div class="col-12 col-xl-6">
              <section class="sync-column sync-column--database h-100">
                <div class="sync-column__title">Tricklist original</div>
                <template v-if="row.database">
                  <dl class="sync-field-list mb-0">
                    <div v-for="field in supportedFieldsForRow(row.csv)" :key="`db-${row.id}-${field}`" class="sync-field" :class="fieldDiffClass(row, field)">
                      <dt>{{ fieldLabel(field) }}</dt>
                      <dd>{{ formatFieldValue(field, row.database?.[field]) }}</dd>
                    </div>
                  </dl>
                </template>
                <div v-else class="sync-empty-state">
                  No existe en la tricklist efectiva actual.
                </div>
              </section>
            </div>
          </div>
        </div>
      </article>
    </div>

    <button
      v-if="showScrollTopButton"
      type="button"
      class="btn btn-dark sync-scroll-top"
      @click="scrollToTop"
    >
      <i class="fa-solid fa-arrow-up"></i>
    </button>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { secureStorage } from '@/services/secureStorage';
import { isSupervisor } from '@/helpers/roleUtils';

const FIELD_LABELS = {
  id: 'ID',
  name: 'Nombre',
  short_name: 'Nombre técnico',
  description: 'Descripción',
  categorie_id: 'Categoría',
  level_id: 'Nivel',
  type_id: 'Tipo',
  value: 'Valor',
  creator: 'Creator',
};

export default {
  name: 'TrickListCsvSyncView',
  data() {
    return {
      loading: false,
      rows: [],
      summary: {
        match: 0,
        diff: 0,
        new_csv_only: 0,
      },
      csvPath: '',
      selectedCsvFile: '',
      availableCsvFiles: [],
      tricklistVersion: null,
      supportedFields: ['id', 'name', 'short_name', 'description', 'categorie_id', 'level_id', 'type_id', 'value', 'creator'],
      searchQuery: '',
      statusFilter: '',
      busyRowIds: [],
      errorMessage: '',
      categories: [],
      levels: [],
      types: [],
      csvUploadFile: null,
      uploadingCsv: false,
      expandedRowIds: [],
      showScrollTopButton: false,
    };
  },
  computed: {
    ...mapState('user', ['user', 'token']),
    isSupervisorUser() {
      return isSupervisor(this.user);
    },
    filteredRows() {
      const query = this.searchQuery.toLowerCase();

      return this.rows.filter((row) => {
        const matchesStatus = !this.statusFilter || row.status === this.statusFilter;
        if (!matchesStatus) {
          return false;
        }

        if (!query) {
          return true;
        }

        const haystack = [
          row.id,
          row.csv?.name,
          row.csv?.short_name,
          row.database?.name,
          row.database?.short_name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return haystack.includes(query);
      });
    },
    versionLabel() {
      if (!this.tricklistVersion) {
        return 'Sin resolver';
      }

      const year = this.tricklistVersion.year ? ` (${this.tricklistVersion.year})` : '';
      return `${this.tricklistVersion.name || 'Versión'}${year}`;
    },
    csvPathLabel() {
      return this.csvPath || 'No informado';
    },
  },
  async mounted() {
    if (!this.isSupervisorUser) {
      this.$router.replace('/home');
      return;
    }

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
    await Promise.all([this.fetchSelectors(), this.fetchComparison()]);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    async fetchSelectors() {
      try {
        const response = await this.$axios.get('/tricks/selectors', {
          headers: { ...this.getAuthHeader() },
        });

        this.categories = response.data?.categories || [];
        this.levels = response.data?.levels || [];
        this.types = response.data?.types || [];
      } catch (error) {
        this.categories = [];
        this.levels = [];
        this.types = [];
      }
    },
    async fetchComparison() {
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await this.$axios.get('/tricks/csv-sync/compare', {
          params: this.selectedCsvFile ? { csv_file: this.selectedCsvFile } : {},
          headers: { ...this.getAuthHeader() },
        });

        this.rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
        this.summary = response.data?.summary || this.summary;
        this.csvPath = response.data?.csv_path || '';
        this.availableCsvFiles = Array.isArray(response.data?.csv_files) ? response.data.csv_files : [];
        this.selectedCsvFile = response.data?.selected_csv_file || this.selectedCsvFile;
        this.tricklistVersion = response.data?.tricklist_version || null;
        this.supportedFields = Array.isArray(response.data?.supported_fields) && response.data.supported_fields.length
          ? response.data.supported_fields
          : this.supportedFields;
        this.expandedRowIds = this.expandedRowIds.filter((id) => this.rows.some((row) => row.id === id));
      } catch (error) {
        this.rows = [];
        this.errorMessage = error?.response?.data?.message || 'No se pudo cargar la comparación CSV.';
      } finally {
        this.loading = false;
      }
    },
    async syncRow(row, action) {
      if (!row?.id || this.isBusy(row.id)) {
        return;
      }

      this.busyRowIds.push(row.id);

      const endpoint = action === 'add'
        ? `/tricks/csv-sync/${row.id}/add`
        : `/tricks/csv-sync/${row.id}`;
      const request = action === 'add'
        ? this.$axios.post(endpoint, this.buildSyncPayload(), { headers: { ...this.getAuthHeader() } })
        : this.$axios.patch(endpoint, this.buildSyncPayload(), { headers: { ...this.getAuthHeader() } });

      try {
        const response = await request;
        this.notifySuccess(response.data?.message || (action === 'add' ? 'Truco añadido correctamente.' : 'Truco actualizado correctamente.'));
        await this.fetchComparison();
      } catch (error) {
        this.notifyError(error?.response?.data?.message || 'No se pudo completar la sincronización.');
      } finally {
        this.busyRowIds = this.busyRowIds.filter((itemId) => itemId !== row.id);
      }
    },
    buildSyncPayload() {
      const payload = this.tricklistVersion?.id
        ? { tricklist_version_id: this.tricklistVersion.id }
        : {};

      if (this.selectedCsvFile) {
        payload.csv_file = this.selectedCsvFile;
      }

      return payload;
    },
    onCsvUploadSelected(event) {
      this.csvUploadFile = event?.target?.files?.[0] || null;
    },
    async uploadCsvFile() {
      if (!this.csvUploadFile || this.uploadingCsv) {
        return;
      }

      this.uploadingCsv = true;
      const formData = new FormData();
      formData.append('csv_file', this.csvUploadFile);

      try {
        const response = await this.$axios.post('/tricks/csv-sync/files', formData, {
          headers: {
            ...this.getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        });

        this.availableCsvFiles = Array.isArray(response.data?.csv_files) ? response.data.csv_files : this.availableCsvFiles;
        this.selectedCsvFile = response.data?.selected_csv_file || this.selectedCsvFile;
        this.csvUploadFile = null;
        this.notifySuccess(response.data?.message || 'CSV subido correctamente.');
        await this.fetchComparison();
      } catch (error) {
        this.notifyError(error?.response?.data?.message || 'No se pudo subir el CSV.');
      } finally {
        this.uploadingCsv = false;
      }
    },
    supportedFieldsForRow(csvRow) {
      const presentFields = this.supportedFields.filter((field) => {
        return field === 'id' || Object.prototype.hasOwnProperty.call(csvRow || {}, field);
      });

      return presentFields.length ? presentFields : this.supportedFields;
    },
    rowClass(status) {
      return {
        'sync-row--match': status === 'match',
        'sync-row--diff': status === 'diff',
        'sync-row--new': status === 'new_csv_only',
      };
    },
    statusBadgeClass(status) {
      return {
        'text-bg-success': status === 'match',
        'text-bg-warning text-dark': status === 'diff',
        'text-bg-primary': status === 'new_csv_only',
      };
    },
    statusLabel(status) {
      if (status === 'match') return 'Coincide';
      if (status === 'diff') return 'Diferencias';
      if (status === 'new_csv_only') return 'Nuevo CSV';
      return status;
    },
    fieldLabel(field) {
      return FIELD_LABELS[field] || field;
    },
    fieldDiffClass(row, field) {
      const hasDiff = Array.isArray(row?.differences) && row.differences.some((item) => item.field === field);
      return {
        'sync-field--diff': hasDiff,
      };
    },
    formatFieldValue(field, value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      if (field === 'categorie_id') {
        return this.findSelectorLabel(this.categories, value);
      }

      if (field === 'level_id') {
        return this.findSelectorLabel(this.levels, value);
      }

      if (field === 'type_id') {
        return this.findSelectorLabel(this.types, value);
      }

      return value;
    },
    findSelectorLabel(items, value) {
      const normalized = Number(value);
      const match = items.find((item) => Number(item.id) === normalized);
      return match ? `${match.name} (${match.id})` : value;
    },
    isBusy(id) {
      return this.busyRowIds.includes(id);
    },
    isRowExpanded(id) {
      return this.expandedRowIds.includes(id);
    },
    toggleRow(id) {
      if (this.isRowExpanded(id)) {
        this.expandedRowIds = this.expandedRowIds.filter((rowId) => rowId !== id);
        return;
      }

      this.expandedRowIds = [...this.expandedRowIds, id];
    },
    handleScroll() {
      this.showScrollTopButton = window.scrollY > 320;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    notifySuccess(message) {
      if (this.$toast?.success) {
        this.$toast.success(message);
        return;
      }

      this.errorMessage = '';
    },
    notifyError(message) {
      if (this.$toast?.error) {
        this.$toast.error(message);
      }

      this.errorMessage = message;
    },
    goBackToTrickList() {
      this.$router.push('/trickList');
    },
    getAuthHeader() {
      const fromState = this.token;
      const fromSecure = secureStorage.getItem('setIn');
      const cleanedSecure = fromSecure ? fromSecure.replace(/"/g, '') : null;
      const legacy = this.$store?.getters?.getToken;
      const token = fromState || cleanedSecure || legacy;
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
  },
};
</script>

<style scoped>
.trick-sync-view {
  max-width: 1440px;
}

.sync-row {
  border-left: 5px solid transparent;
}

.sync-row__header {
  cursor: pointer;
}

.sync-row__header:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 4px;
  border-radius: 8px;
}

.sync-row--match {
  border-left-color: #198754;
}

.sync-row--diff {
  border-left-color: #ffc107;
}

.sync-row--new {
  border-left-color: #0d6efd;
}

.sync-column {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  background: #fff;
}

.sync-column__title {
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.75rem;
}

.sync-field-list {
  display: grid;
  gap: 0.5rem;
}

.sync-field {
  display: grid;
  grid-template-columns: minmax(120px, 180px) minmax(0, 1fr);
  gap: 0.75rem;
  align-items: start;
  border-radius: 10px;
  padding: 0.5rem 0.625rem;
}

.sync-field dt {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
}

.sync-field dd {
  margin: 0;
  word-break: break-word;
}

.sync-field--diff {
  background: rgba(255, 193, 7, 0.18);
}

.sync-empty-state {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #6c757d;
  padding: 2rem 1rem;
  border: 1px dashed #ced4da;
  border-radius: 12px;
}

.sync-scroll-top {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 1030;
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
}

@media (max-width: 767.98px) {
  .sync-field {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }

  .sync-scroll-top {
    bottom: 16px;
  }
}
</style>